import { ProgressCTA } from "@/components/home/ProgressCTA/ProgressCTA";
import { createFileRoute } from "@tanstack/react-router";
import { HeartPulse } from "lucide-react";
import { seo } from "@/utilities/seo";
import { PATHS } from "@/config/paths";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/_home/aboutUs")({
  head: () => ({
    meta: [
      { title: "About Us - ProgressMade.ai" },
      ...seo({
        title: "About Us - ProgressMade.ai",
        description:
          "Learn about ProgressMade.ai, our mission to empower your health journey, and the story behind our founder. Discover how our community and platform help you build sustainable routines for sleep, nutrition, and movement.",
        keywords:
          "About ProgressMade, Health Community, Health Goals, Wellness, Sleep, Nutrition, Movement, Founder Story, Health Platform, Sustainable Routines, Well-being, ProgressMade.ai",
        image: "https://progressmade.ai/thijs.webp",
        canonical: "https://progressmade.ai/about-us",
      }),
    ],
  }),
  component: AboutUsPage,
});

function AboutUsPage() {
  return (
    <>
      <section id="about-us" className="col-span-12 space-y-8 grid grid-cols-12">
        <div className="col-span-12 flex flex-col items-center gap-4 mb-6 md:mb-12 text-center">
          <HeartPulse className="h-8 w-8 text-gray-700" />
          <Heading level="h1" size="h1" className="flex gap-2">
            About Us
          </Heading>
        </div>

        <div className="col-span-12">
          <div className="flex flex-col items-center my-4">
            <img
              src="/thijs.webp"
              alt="Thijs Smudde, founder of ProgressMade.ai"
              className="w-40 h-40 object-cover rounded-full shadow-md mb-1 border-4 border-white dark:border-background"
              width={160}
              height={160}
            />
            <span className="text-xs text-muted-foreground mt-1">Founder of ProgressMade</span>
            <span className="text-base font-medium text-foreground">Thijs Smudde</span>
          </div>
        </div>

        <div className="col-span-12 md:col-span-10 md:col-start-2 xl:col-span-8 xl:col-start-3">
          <div className="text-left w-full">
            <p className="text-lg text-pretty leading-8 text-foreground mb-4">
              ProgressMade was born from a deeply held belief: that our health is the most valuable
              asset we possess. It's the foundation upon which we build every aspect of our lives,
              and yet, it's something we often take for granted until it's compromised.
            </p>
            <p className="text-lg text-pretty leading-8 text-foreground mb-4">
              For me, running has been a lifelong passion. It's taught me the power of consistent
              effort, the importance of listening to my body, and the incredible feeling of
              progress. Now that I am in my 30s, my commitment to staying active has only deepened,
              fueling a desire to find sustainable routines that support long-term well-being and
              performance.
            </p>
            <p className="text-lg text-pretty leading-8 text-foreground mb-4">
              This personal journey sparked a broader realization: in the rush of modern life, we
              often focus on treating the <span className="italic">symptoms</span> of poor health
              rather than addressing the root causes. We seek quick fixes instead of investing in
              the fundamental pillars of a healthy life – sleep, nutrition, and movement.
            </p>
            <p className="text-lg text-pretty leading-8 text-foreground mb-4">
              I envisioned a place where people could shift their focus, a platform dedicated to
              empowering individuals to proactively invest in themselves. ProgressMade is that
              vision brought to life. It's designed to be a supportive space where you can set
              meaningful health goals, track your progress, and build sustainable routines that
              truly enrich your life.
            </p>
            <p className="text-lg text-pretty leading-8 text-foreground mb-4">
              But ProgressMade is more than just a tracking app. It's a community. We believe that
              sharing our journeys, celebrating our successes, and learning from each other's
              experiences is a powerful catalyst for change. Whether you're a seasoned athlete or
              just starting your health journey, you'll find a welcoming and inclusive environment
              here.
            </p>
            <p className="text-lg text-pretty leading-8 text-foreground mb-4">
              Our ultimate aim is to contribute to a healthier society, one person at a time. By
              providing the tools and community to make health a priority, we hope to inspire a
              ripple effect of positive change. Your dedication to your own well-being can become an
              inspiration for others, showing them the profound impact of investing in themselves.
            </p>
            <p className="text-lg text-pretty leading-8 text-foreground">
              At ProgressMade, we believe that your sleep, nutrition, and movement goals are not
              just tasks to complete; they are the building blocks of your healthiest, most vibrant
              self. Join us in building a stronger foundation for a lifetime of well-being.
            </p>
          </div>
        </div>
      </section>
      <ProgressCTA />
    </>
  );
}
